import type { TBTStatusBadgeDisplayTypes } from "@btnet/core-components/BTStatusBadge";

import type { SearchDialogRowKind } from "./rows/searchDialogRow.types";
import type { AskArmedBy } from "./searchDialog.learn";

/**
 * Semantic polarity for a status chip or a stat tile. A token, never a color, so
 * the mapping below owns rendering and a server can send polarity without
 * sending a hex value that would break in dark mode.
 */
export type AgentTone = "neutral" | "positive" | "negative" | "warning";

const TONE_DISPLAY_TYPE: Record<AgentTone, TBTStatusBadgeDisplayTypes> = {
    neutral: "default",
    positive: "success",
    negative: "danger",
    warning: "warning",
};

export function toneToDisplayType(tone: AgentTone): TBTStatusBadgeDisplayTypes {
    return TONE_DISPLAY_TYPE[tone];
}

/**
 * Which of the dialog's two views is on screen. Derived from the query alone,
 * never set by a caller. Arming Ask changes what Enter does, not what renders.
 */
export type SearchDialogView = { kind: "browse" } | { kind: "results"; query: string };

export interface IResolveViewInput {
    /** The live input value. */
    query: string;
}

const QUESTION_OPENERS =
    /^(what|which|who|when|where|why|how|show|find|do i|am i|can i|is there|are there)\b/i;

/**
 * Whether a query reads as a question rather than a name. Deliberately shallow,
 * because it only decides which key does what — a false positive costs a
 * keystroke, not a wrong action.
 */
export function looksLikeQuestion(query: string): boolean {
    const trimmed = query.trim();
    return trimmed.includes("?") || QUESTION_OPENERS.test(trimmed);
}

export interface IResolveAskModeInput {
    /** The user's own toggle, which always wins once they have touched it. */
    userToggled: boolean;
    query: string;
    /** True once a search has completed and matched nothing. */
    zeroResults: boolean;
}

/**
 * Whether Enter asks the agent rather than opening the highlighted row.
 *
 * Auto-arms on a question-shaped query, or one that matched nothing, since a
 * natural-language question rarely matches a record by name. Arming only
 * changes what Enter does, and the footer says which.
 */
export function resolveAskMode(input: IResolveAskModeInput): boolean {
    return resolveAskArmedBy(input) !== null;
}

/**
 * What armed Ask, or null when it is not armed. The same rules as
 * {@link resolveAskMode}, which reads this, so the two cannot disagree.
 *
 * A question-shaped query arms before its search has finished, so when it
 * also matches nothing the cause is still `auto`.
 */
export function resolveAskArmedBy(input: IResolveAskModeInput): AskArmedBy | null {
    if (input.userToggled) {
        return "toggle";
    }
    if (input.query.trim().length === 0) {
        return null;
    }
    if (looksLikeQuestion(input.query)) {
        return "auto";
    }
    return input.zeroResults ? "zero-results" : null;
}

/** What a bare Enter does right now. `none` is a real state: nothing to open, ask or retry. */
export type SearchDialogEnterAction = "open" | "ask" | "search" | "retry" | "none";

export interface IResolveEnterActionInput {
    /** The highlighted row's kind, or null while Ask or nothing holds the highlight. */
    highlightedKind: SearchDialogRowKind | null;
    askMode: boolean;
    hasQuery: boolean;
    /** The search on screen failed in a way another try could fix. */
    canRetry: boolean;
}

/** Mirrors the dialog's Enter handler, so the footer can say what Enter will do. */
export function resolveEnterAction(input: IResolveEnterActionInput): SearchDialogEnterAction {
    if (input.highlightedKind === "record") {
        return "open";
    }
    if (input.highlightedKind === "prompt") {
        return "ask";
    }
    if (input.highlightedKind === "search") {
        return "search";
    }
    if (input.askMode) {
        return input.hasQuery ? "ask" : "none";
    }
    return input.hasQuery && input.canRetry ? "retry" : "none";
}

/** Derives the current view. See {@link SearchDialogView} for why this is derived. */
export function resolveView(input: IResolveViewInput): SearchDialogView {
    // Cleared, or freshly opened; either way the user is browsing.
    if (input.query.trim().length === 0) {
        return { kind: "browse" };
    }

    return { kind: "results", query: input.query };
}
